import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Chip, Grid, ListItemText, MenuItem, Select, SelectChangeEvent, styled } from '@mui/material';
import useTrackingCookies from 'parent-portal/shared/EngagementSourceTracking';
import usePublicSessionTracking from 'parent-portal/shared/PublicSessionTracking';
import { client, consoleLogInDev } from 'shared';
import { Colors, Text } from 'library';
import useQueryParams from 'shared/QueryParamsHook';
import { Environment } from 'environmentVariables';
import {
    PageWrapper,
    HeroSection,
    HeroContent,
    ContentSection,
    TypeChip,
    ChipRow,
    LocationTag,
    getStateAbbr,
    formatStateName,
    parseJobTypeLabels,
} from './shared';

interface IIndeedJob {
    id: number;
    city: string;
    state: string;
    zip_code: string;
    title: string;
    alt_title: string;
    pay_string: string;
    job_type: string;
    description_preview: string;
}

interface IFilters {
    states: string[];
    cities_by_state: Record<string, string[]>;
}

function navigateWithState(state: string) {
    const params = new URLSearchParams();
    if (state) params.set('state', state);
    const qs = params.toString();
    window.location.href = qs ? `listings?${qs}` : 'listings';
}

function buildCityParams(state: string, cities: string[]): string {
    const params = new URLSearchParams();
    if (state) params.set('state', state);
    cities.forEach((c) => params.append('city', c));
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}

export default function Workers() {
    useTrackingCookies('/workers');
    const { usePageLoadTracking } = usePublicSessionTracking('workers');
    usePageLoadTracking();
    const query = useQueryParams();
    const [jobs, setJobs] = useState<IIndeedJob[]>([]);
    const [filters, setFilters] = useState<IFilters>({ states: [], cities_by_state: {} });
    const selectedState = query.get('state') || '';
    const initialCities = query.getAll('city');
    const [pendingCities, setPendingCities] = useState<string[]>(initialCities);

    useEffect(() => {
        client('api/indeed-jobs/list-jobs/filters/')
            .then((response: IFilters) => setFilters(response))
            .catch((error: unknown) => consoleLogInDev(error));
    }, []);

    useEffect(() => {
        const qs = buildCityParams(selectedState, initialCities);
        client(`api/indeed-jobs/list-jobs/${qs}`)
            .then((response: IIndeedJob[]) => setJobs(response))
            .catch((error: unknown) => consoleLogInDev(error));
    }, [selectedState, initialCities.join(',')]);

    const availableCities = useMemo(() => {
        if (!selectedState) {
            const allCities = Object.values(filters.cities_by_state).flat();
            return Array.from(new Set(allCities)).sort();
        }
        return filters.cities_by_state[selectedState] || [];
    }, [selectedState, filters]);

    function handleCitiesChange(e: SelectChangeEvent<string[]>) {
        const value = e.target.value;
        setPendingCities(typeof value === 'string' ? value.split(',') : value);
    }

    function handleCitiesClose() {
        if (pendingCities.join(',') !== initialCities.join(',')) {
            const qs = buildCityParams(selectedState, pendingCities);
            window.location.href = qs ? `listings${qs}` : 'listings';
        }
    }

    const hasFilters = selectedState || initialCities.length > 0;

    return (
        <PageWrapper>
            <HeroSection style={{ padding: '48px 24px' }}>
                <HeroContent>
                    <Text variant="h1" textStyle={{ color: Colors.white, fontSize: 36, fontWeight: 700 }}>
                        Join Our Team
                    </Text>
                    <Text
                        variant="body1"
                        textStyle={{
                            color: Colors.white70percent,
                            fontSize: 18,
                            marginTop: 8,
                            maxWidth: 600,
                        }}
                    >
                        Find childcare teaching positions near you with competitive pay, flexible scheduling, and daily
                        pay options.
                    </Text>
                </HeroContent>
            </HeroSection>

            <ContentSection style={{ padding: '24px 24px 48px' }}>
                <FilterBar>
                    <FilterDropdown
                        value={selectedState}
                        onChange={(e) => navigateWithState(e.target.value as string)}
                        displayEmpty
                        variant="outlined"
                    >
                        <MenuItem value="">All States</MenuItem>
                        {filters.states.map((state) => (
                            <MenuItem key={state} value={state}>
                                {formatStateName(state)}
                            </MenuItem>
                        ))}
                    </FilterDropdown>
                    <FilterDropdown
                        multiple
                        value={pendingCities}
                        onChange={handleCitiesChange as (e: SelectChangeEvent<unknown>) => void}
                        onClose={handleCitiesClose}
                        displayEmpty
                        variant="outlined"
                        MenuProps={{
                            disableScrollLock: true,
                            PaperProps: { style: { maxHeight: 350 } },
                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                            transformOrigin: { vertical: 'top', horizontal: 'left' },
                        }}
                        renderValue={(selected) => {
                            const sel = selected as string[];
                            if (sel.length === 0) return <PlaceholderText>All Cities</PlaceholderText>;
                            const label = sel.length === 1 ? sel[0] : `${sel.length} cities selected`;
                            return <span>{label}</span>;
                        }}
                    >
                        {availableCities.map((city) => (
                            <MenuItem key={city} value={city}>
                                <Checkbox checked={pendingCities.includes(city)} size="small" />
                                <ListItemText primary={city} />
                            </MenuItem>
                        ))}
                    </FilterDropdown>
                    {hasFilters && <ClearLink href="listings">Clear filters</ClearLink>}
                </FilterBar>

                <ResultsCount>
                    {jobs.length} {jobs.length === 1 ? 'position' : 'positions'} available
                    {selectedState ? ` in ${formatStateName(selectedState)}` : ''}
                    {initialCities.length > 0 ? `, ${initialCities.join(', ')}` : ''}
                </ResultsCount>

                <Grid container spacing={3}>
                    {jobs.map((x) => (
                        <JobListingCard key={x.id} job={x} />
                    ))}
                </Grid>
                {!jobs.length && (
                    <EmptyState>
                        <Text variant="h2" textStyle={{ marginBottom: 8 }}>
                            No positions found
                        </Text>
                        <Text variant="body1" textStyle={{ color: Colors.mediumGrey }}>
                            Try adjusting your filters or check back soon for new openings.
                        </Text>
                    </EmptyState>
                )}
            </ContentSection>
        </PageWrapper>
    );
}

function JobListingCard({ job }: { job: IIndeedJob }) {
    const typeLabels = parseJobTypeLabels(job.job_type);

    return (
        <Grid item xs={12} sm={6} md={4}>
            <JobCard>
                <CardContent>
                    <LocationTag>
                        {job.city}, {getStateAbbr(job.state)}
                    </LocationTag>
                    <JobTitle
                        href={
                            Environment === 'development'
                                ? `http://localhost:8000/sitter/indeed-job/${job.id}/`
                                : `/job-listings/${job.id}`
                        }
                    >
                        {job.alt_title}
                    </JobTitle>
                    <ChipRow style={{ marginBottom: 12 }}>
                        {typeLabels.map((label) => {
                            const isFullTime = label.toLowerCase().includes('full');
                            return (
                                <TypeChip key={label} $variant={isFullTime ? 'full' : 'part'}>
                                    {label}
                                </TypeChip>
                            );
                        })}
                    </ChipRow>
                    {job.description_preview && <DescriptionPreview>{job.description_preview}...</DescriptionPreview>}
                </CardContent>
                <CardFooter>
                    <PayText>{job.pay_string}</PayText>
                    <CardApplyButton href={`/application/apply/${job.id}`}>Apply Now</CardApplyButton>
                </CardFooter>
            </JobCard>
        </Grid>
    );
}

const FilterBar = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 24,
    flexWrap: 'wrap' as const,
});

const FilterDropdown = styled(Select)({
    minWidth: 180,
    fontFamily: 'Outfit',
    backgroundColor: Colors.white,
    borderRadius: 8,
    fontSize: 14,
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: Colors.lightGrey,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: Colors.mediumGrey,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: Colors.darkNavy,
    },
    '& .MuiSelect-select': {
        padding: '10px 14px',
        fontFamily: 'Outfit',
    },
});

const ClearLink = styled('a')({
    fontSize: 13,
    color: Colors.mediumGrey,
    textDecoration: 'underline',
    fontFamily: 'Outfit',
    cursor: 'pointer',
    '&:hover': {
        color: Colors.darkNavy,
    },
});

const ResultsCount = styled('div')({
    fontSize: 14,
    color: Colors.mediumGrey,
    marginBottom: 20,
    fontFamily: 'Outfit',
});

const JobCard = styled('div')({
    height: '100%',
    borderRadius: 12,
    border: `1px solid ${Colors.lightGrey}`,
    backgroundColor: Colors.white,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
    overflow: 'hidden',
    '&:hover': {
        boxShadow: '0 8px 24px rgba(0,47,77,0.12)',
        transform: 'translateY(-2px)',
    },
});

const CardContent = styled('div')({
    padding: '20px 20px 12px',
    flex: 1,
});

const JobTitle = styled('a')({
    color: Colors.darkNavy,
    fontWeight: 700,
    fontSize: 18,
    textDecoration: 'none',
    fontFamily: 'Outfit',
    display: 'block',
    marginBottom: 10,
    lineHeight: 1.3,
    '&:hover': {
        color: Colors.turquoise,
    },
});

const DescriptionPreview = styled('p')({
    fontSize: 13,
    lineHeight: 1.5,
    color: Colors.mediumGrey,
    fontFamily: 'Outfit',
    margin: 0,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
});

const CardFooter = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px 16px',
    borderTop: `1px solid ${Colors.lightGrey}`,
});

const PayText = styled('span')({
    fontWeight: 700,
    fontSize: 15,
    color: Colors.darkNavy,
    fontFamily: 'Outfit',
});

const CardApplyButton = styled('a')({
    borderRadius: 20,
    padding: '8px 20px',
    fontSize: 14,
    fontWeight: 600,
    backgroundColor: Colors.turquoise,
    color: Colors.white,
    textDecoration: 'none',
    fontFamily: 'Outfit',
    transition: 'background-color 0.15s ease',
    '&:hover': {
        backgroundColor: '#008a76',
        color: Colors.white,
    },
});

const EmptyState = styled('div')({
    textAlign: 'center' as const,
    padding: '60px 20px',
});

const PlaceholderText = styled('span')({
    color: Colors.mediumGrey,
});

const ChipContainer = styled('div')({
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 4,
});

const chipStyles = {
    fontFamily: 'Outfit',
    fontSize: 12,
    height: 24,
    backgroundColor: Colors.lightTurq,
    color: Colors.darkNavy,
};
